// Tabla chica de acciones del asistente (detalle de un correo y de un caso).
import { useState } from 'react';
import { Check, X as XIcon } from 'lucide-react';
import { fmtDateTime, toolLabel, isSimulated, actionDetailText, type ActionRow } from './shared';

const DETAIL_MAX = 140;

/** Resultado de una acción: ✓ / ✗ / — y la marca gris "simulado". */
export function ActionResult({ a }: { a: ActionRow }) {
  const sim = isSimulated(a);
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      {a.ok === true ? (
        <Check size={14} className="text-[var(--color-success)]" aria-label="Salió bien" />
      ) : a.ok === false ? (
        <XIcon size={14} className="text-[var(--color-error)]" aria-label="Falló" />
      ) : (
        <span className="text-[var(--color-text-muted)]">—</span>
      )}
      {sim && (
        <span
          className="px-1.5 py-0.5 rounded text-[10px] bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)]"
          title="Modo aprendizaje: se registró lo que haría, sin ejecutarlo"
        >
          simulado
        </span>
      )}
    </span>
  );
}

/** Detalle recortado con "ver más". */
export function ActionDetail({ a }: { a: ActionRow }) {
  const [open, setOpen] = useState(false);
  const text = actionDetailText(a);
  if (!text) return <span className="text-[var(--color-text-muted)]">—</span>;
  if (text.length <= DETAIL_MAX || open) {
    return (
      <span className="break-words whitespace-pre-wrap">
        {text}
        {open && (
          <button type="button" onClick={() => setOpen(false)} className="ml-1 text-[var(--color-primary)] hover:underline">
            ver menos
          </button>
        )}
      </span>
    );
  }
  return (
    <span className="break-words">
      {text.slice(0, DETAIL_MAX)}…{' '}
      <button type="button" onClick={() => setOpen(true)} className="text-[var(--color-primary)] hover:underline">
        ver más
      </button>
    </span>
  );
}

export function ActionsMini({ actions }: { actions: ActionRow[] | null | undefined }) {
  const list = actions ?? [];
  if (list.length === 0) {
    return <div className="text-xs text-[var(--color-text-muted)]">Sin acciones registradas.</div>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
      <table className="w-full text-xs">
        <thead className="bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)]">
          <tr>
            <th className="text-left px-2 py-1.5 font-medium">Fecha</th>
            <th className="text-left px-2 py-1.5 font-medium">Qué hizo</th>
            <th className="text-left px-2 py-1.5 font-medium">Resultado</th>
            <th className="text-left px-2 py-1.5 font-medium">Detalle</th>
          </tr>
        </thead>
        <tbody>
          {list.map((a) => (
            <tr key={a.id} className="border-t border-[var(--color-border)] align-top text-[var(--color-text)]">
              <td className="px-2 py-1.5 whitespace-nowrap text-[var(--color-text-secondary)]">
                {fmtDateTime(a.created_at)}
              </td>
              <td className="px-2 py-1.5">{toolLabel(a.tool)}</td>
              <td className="px-2 py-1.5">
                <ActionResult a={a} />
              </td>
              <td className="px-2 py-1.5 min-w-[160px]">
                <ActionDetail a={a} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
